/* House of Plants — interactions
   Progressive enhancement: with JS off the page still reads top to bottom,
   anchors still jump, rails still scroll natively and the FAQ still opens
   (it is built on <details>). */
(function () {
  'use strict';

  // Every section on the page requests this file, so the browser runs it once
  // per <script> tag. Everything below should only ever wire up once.
  if (window.__hopBooted) return;
  window.__hopBooted = true;

  var reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
  var canHover = window.matchMedia('(hover: hover) and (pointer: fine)');

  function scrollBehavior() {
    return reduceMotion.matches ? 'auto' : 'smooth';
  }

  /* Keep anchor targets clear of whatever is pinned over the top of the
     viewport. Matching on header-ish class names missed this theme's header,
     so instead ask the page what is actually painting up there: sample a few
     points near the top and measure the lowest fixed/sticky thing under them.
     Name-agnostic, so it survives the theme changing its markup. */
  function overlayBottom() {
    var bottom = 0;
    var xs = [Math.round(window.innerWidth / 2), 10, window.innerWidth - 10];
    for (var i = 0; i < xs.length; i++) {
      for (var y = 2; y <= 190; y += 12) {
        var el = document.elementFromPoint(xs[i], y);
        var hops = 0;
        while (el && el !== document.body && el !== document.documentElement && hops++ < 12) {
          var pos = getComputedStyle(el).position;
          if (pos === 'fixed' || pos === 'sticky') {
            var r = el.getBoundingClientRect();
            // pinned to the top, and a plausible header rather than a full-screen layer
            if (r.top <= 4 && r.bottom > bottom && r.bottom < 260) bottom = r.bottom;
            break;
          }
          el = el.parentElement;
        }
      }
    }
    return bottom;
  }

  function syncAnchorOffset() {
    document.documentElement.style.setProperty(
      '--hop-anchor-offset', Math.round(overlayBottom() + 16) + 'px'
    );
  }

  /* Native smooth scrolling takes about a second to cross this page, which
     reads as lag. This eases over ~400ms instead, and any touch or wheel
     cancels it so the shopper is never fighting an animation. */
  var scrollRAF = null;
  function cancelScroll() {
    if (scrollRAF) { cancelAnimationFrame(scrollRAF); scrollRAF = null; }
  }
  ['wheel', 'touchstart', 'pointerdown'].forEach(function (evt) {
    window.addEventListener(evt, cancelScroll, { passive: true });
  });

  function anchorOffset() {
    var v = parseInt(getComputedStyle(document.documentElement)
      .getPropertyValue('--hop-anchor-offset'), 10);
    return isNaN(v) ? 80 : v;
  }

  /* The header is only pinned once the page has moved, so its height reads as
     zero at click time. Correct the resting position after the scroll lands
     (and once more a moment later, in case the header animates in). */
  function settle(el) {
    var want = overlayBottom() + 16;
    var delta = el.getBoundingClientRect().top - want;
    if (Math.abs(delta) > 2) window.scrollBy(0, delta);
    syncAnchorOffset();
  }

  function smoothTo(el) {
    syncAnchorOffset();
    var y = Math.max(0, window.scrollY + el.getBoundingClientRect().top - anchorOffset());
    var start = window.scrollY, dist = y - start;

    function done() {
      settle(el);
      setTimeout(function () { settle(el); }, 140);
    }

    if (reduceMotion.matches || Math.abs(dist) < 4) { window.scrollTo(0, y); done(); return; }
    cancelScroll();
    var dur = Math.min(430, Math.max(260, Math.abs(dist) * 0.2));
    var t0 = performance.now();
    (function step(t) {
      var p = Math.min(1, (t - t0) / dur);
      var e = 1 - Math.pow(1 - p, 3);
      window.scrollTo(0, start + dist * e);
      if (p < 1) { scrollRAF = requestAnimationFrame(step); }
      else { scrollRAF = null; done(); }
    })(t0);
  }

  function goToHouse(key) {
    if (!key) return;
    var target = key.charAt(0) === '#'
      ? document.querySelector(key)
      : document.getElementById('house-' + key);
    if (!target) return;
    smoothTo(target);
    target.setAttribute('tabindex', '-1');
    target.focus({ preventScroll: true });
  }

  /* A colour pick is a request to see plants, so go to the product row
     itself rather than to the top of the house and its write-up. */
  function goToPlants(key) {
    var section = document.getElementById('house-' + key);
    if (!section) return;
    smoothTo(section.querySelector('.hop-rail') || section);
    section.setAttribute('tabindex', '-1');
    section.focus({ preventScroll: true });
  }

  /* 1. Anything that points at a house ------------------------------------ */
  function bindScrollTriggers(root) {
    root.querySelectorAll('[data-hop-goto]:not(.hop-dot)').forEach(function (el) {
      if (el.dataset.hopBound) return;
      el.dataset.hopBound = '1';
      el.addEventListener('click', function (e) {
        e.preventDefault();
        goToHouse(el.dataset.hopGoto);
      });
    });
  }

  /* 2. Colour picker ------------------------------------------------------- */
  function initPicker(picker) {
    if (picker.dataset.hopBound) return;
    picker.dataset.hopBound = '1';

    var dots = picker.querySelectorAll('.hop-dot');
    var persona = picker.querySelector('[data-hop-persona]');

    function show(dot) {
      if (!persona) return;
      persona.textContent = dot.dataset.persona;
      persona.style.color = dot.dataset.colour;
      persona.classList.add('is-on');
      picker.classList.add('is-active');
      dots.forEach(function (d) { d.classList.toggle('is-on', d === dot); });
    }

    function clear() {
      if (!persona) return;
      persona.classList.remove('is-on');
      picker.classList.remove('is-active');
      dots.forEach(function (d) { d.classList.remove('is-on'); });
    }

    dots.forEach(function (dot) {
      dot.addEventListener('mouseenter', function () { show(dot); });
      dot.addEventListener('focus', function () { show(dot); });
      dot.addEventListener('mouseleave', function () { if (canHover.matches) clear(); });
      dot.addEventListener('blur', function () { if (canHover.matches) clear(); });
      // On touch there is no hover, so reveal on finger-down and let the single
      // tap travel. One tap, never two.
      dot.addEventListener('pointerdown', function () { show(dot); });
      dot.addEventListener('click', function (e) {
        e.preventDefault();
        goToPlants(dot.dataset.hopGoto);
      });
    });
  }

  /* 3. Lazy images inside a horizontal row --------------------------------- */
  /* An image parked off to the right of a scroller can sit undecoded until it
     is swiped to, which reads as a blank card. Once the row itself is on
     screen, promote everything in it so the pictures are already there. */
  function primeImages(rail) {
    rail.querySelectorAll('img[loading="lazy"]').forEach(function (img) {
      img.loading = 'eager';
    });
  }
  function watchImages(rail) {
    if (!('IntersectionObserver' in window)) { primeImages(rail); return; }
    var io = new IntersectionObserver(function (entries) {
      entries.forEach(function (e) {
        if (!e.isIntersecting) return;
        primeImages(rail);
        io.disconnect();
      });
    }, { rootMargin: '400px 0px' });
    io.observe(rail);
  }

  /* 4. Horizontal rails ---------------------------------------------------- */
  function initRail(rail) {
    if (rail.dataset.hopBound) return;
    rail.dataset.hopBound = '1';

    var isMarquee = rail.hasAttribute('data-hop-marquee');
    var nav = document.querySelector('[data-hop-railnav="' + rail.dataset.hopRail + '"]');
    var prev = nav && nav.querySelector('[data-dir="prev"]');
    var next = nav && nav.querySelector('[data-dir="next"]');

    watchImages(rail);

    function step() {
      var first = rail.firstElementChild;
      if (!first) return rail.clientWidth * 0.8;
      var gap = parseFloat(getComputedStyle(rail).columnGap) || 16;
      return first.getBoundingClientRect().width + gap;
    }

    function sync() {
      if (!nav) return;
      // A marquee loops forever, so neither arrow is ever a dead end.
      if (isMarquee) { nav.hidden = false; return; }
      var max = rail.scrollWidth - rail.clientWidth - 2;
      if (prev) prev.disabled = rail.scrollLeft <= 2;
      if (next) next.disabled = rail.scrollLeft >= max;
      nav.hidden = max <= 2;
    }

    function nudge(dir) {
      if (rail.__hopPause) rail.__hopPause(4000);
      rail.scrollBy({ left: dir * step(), behavior: scrollBehavior() });
    }
    if (prev) prev.addEventListener('click', function () { nudge(-1); });
    if (next) next.addEventListener('click', function () { nudge(1); });

    rail.addEventListener('scroll', function () {
      window.requestAnimationFrame(sync);
    }, { passive: true });
    window.addEventListener('resize', sync);
    window.addEventListener('load', sync);
    if ('ResizeObserver' in window) new ResizeObserver(sync).observe(rail);
    rail.querySelectorAll('img').forEach(function (img) {
      if (!img.complete) img.addEventListener('load', sync, { once: true });
    });
    sync();

    /* Drag to pan with a mouse. Touch scrolls natively and is left alone so
       momentum stays native. */
    if (canHover.matches) {
      var down = false, startX = 0, startLeft = 0, moved = 0;
      rail.addEventListener('pointerdown', function (e) {
        if (e.pointerType !== 'mouse') return;
        down = true; moved = 0;
        startX = e.clientX;
        startLeft = rail.scrollLeft;
      });
      rail.addEventListener('pointermove', function (e) {
        if (!down) return;
        var dx = e.clientX - startX;
        if (Math.abs(dx) > 4) { moved = Math.abs(dx); rail.classList.add('is-dragging'); }
        rail.scrollLeft = startLeft - dx;
      });
      ['pointerup', 'pointercancel', 'pointerleave'].forEach(function (evt) {
        rail.addEventListener(evt, function () {
          if (!down) return;
          down = false;
          // A real drag should not also open a quick view or add to cart.
          if (moved > 4) {
            rail.addEventListener('click', function swallow(ev) {
              ev.stopPropagation();
              ev.preventDefault();
            }, { capture: true, once: true });
          }
          rail.classList.remove('is-dragging');
        });
      });
    }

    if (isMarquee) initMarquee(rail);
  }

  /* 5. Continuous marquee -------------------------------------------------- */
  /* The row renders its cards twice. We advance scrollLeft a little every
     frame and, once a full set has passed, subtract exactly that set's width —
     which lands on an identical card, so the loop is invisible. */
  function initMarquee(rail) {
    var SPEED = 30; // px per second

    function setWidth() {
      var kids = rail.children, n = kids.length / 2;
      if (n < 1 || kids.length % 2) return 0;
      return kids[n].offsetLeft - kids[0].offsetLeft;
    }

    var paused = false, resumeTimer;
    function pause(ms) {
      paused = true;
      clearTimeout(resumeTimer);
      if (ms) resumeTimer = setTimeout(function () { paused = false; }, ms);
    }
    function resume() { clearTimeout(resumeTimer); paused = false; }
    rail.__hopPause = pause;

    // Hovering means they are reading or aiming for a card — hold still.
    rail.addEventListener('pointerenter', function () { if (canHover.matches) pause(0); });
    rail.addEventListener('pointerleave', function () { if (canHover.matches) resume(); });
    // Any deliberate interaction wins; drift returns a few seconds later.
    ['pointerdown', 'touchstart', 'wheel'].forEach(function (evt) {
      rail.addEventListener(evt, function () { pause(3500); }, { passive: true });
    });
    rail.addEventListener('focusin', function () { pause(8000); });

    if (reduceMotion.matches) return;

    var last = 0;
    function frame(t) {
      var dt = last ? (t - last) / 1000 : 0;
      last = t;
      // dt guard: after a background tab or a long paint, don't lurch forward.
      if (!paused && dt > 0 && dt < 0.2 && document.visibilityState === 'visible') {
        rail.scrollLeft += SPEED * dt;
      }
      var w = setWidth();
      if (w > 0) {
        if (rail.scrollLeft >= w) rail.scrollLeft -= w;
        else if (rail.scrollLeft < 0) rail.scrollLeft += w;
      }
      window.requestAnimationFrame(frame);
    }
    window.requestAnimationFrame(frame);
  }

  /* 6. Quantity steppers --------------------------------------------------- */
  function initQty() {
    document.addEventListener('click', function (e) {
      var btn = e.target.closest && e.target.closest('.hop-qty__btn');
      if (!btn) return;
      e.preventDefault();
      e.stopPropagation();
      var wrap = btn.closest('.hop-qty');
      var input = wrap && wrap.querySelector('.hop-qty__val');
      if (!input) return;
      var v = parseInt(input.value, 10);
      if (isNaN(v) || v < 1) v = 1;
      v += (btn.name === 'plus' ? 1 : -1);
      if (v < 1) v = 1;
      if (v > 99) v = 99;
      input.value = v;
      input.dispatchEvent(new Event('change', { bubbles: true }));
    });
    // A typed-in blank or zero must never post as a broken quantity.
    document.addEventListener('change', function (e) {
      if (!e.target.matches || !e.target.matches('.hop-qty__val')) return;
      var v = parseInt(e.target.value, 10);
      if (isNaN(v) || v < 1) v = 1;
      if (v > 99) v = 99;
      e.target.value = v;
    });
  }

  /* 7. Scroll reveal ------------------------------------------------------- */
  function initReveal(root) {
    var items = root.querySelectorAll('.hop-reveal:not([data-hop-seen])');
    if (!items.length) return;
    if (reduceMotion.matches || !('IntersectionObserver' in window)) {
      items.forEach(function (el) { el.classList.add('is-in'); });
      return;
    }
    var io = new IntersectionObserver(function (entries) {
      entries.forEach(function (entry) {
        if (!entry.isIntersecting) return;
        entry.target.classList.add('is-in');
        io.unobserve(entry.target);
      });
    }, { rootMargin: '0px 0px -8% 0px', threshold: 0.04 });
    items.forEach(function (el) { el.dataset.hopSeen = '1'; io.observe(el); });
  }

  /* Boot ------------------------------------------------------------------- */
  var qtyReady = false;
  function init(root) {
    root = root || document;
    bindScrollTriggers(root);
    root.querySelectorAll('[data-hop-picker]').forEach(initPicker);
    root.querySelectorAll('[data-hop-rail]').forEach(initRail);
    if (!qtyReady) { initQty(); qtyReady = true; }
    initReveal(root);
    syncAnchorOffset();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { init(); });
  } else {
    init();
  }

  window.addEventListener('resize', syncAnchorOffset);
  document.addEventListener('shopify:section:load', function (e) { init(e.target); });
})();
